import React, { useState, useEffect } from 'react';
import {
  LuShoppingBag,
  LuMapPin,
  LuPhone,
  LuUser,
  LuCircleCheck,
  LuLoader,
  LuMessageCircle,
  LuSparkles
} from 'react-icons/lu';
import { GiBookmarklet, GiOpenBook } from 'react-icons/gi';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../shared/firebase';

export default function BookShop() {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    async function fetchBookShops() {
      try {
        setLoading(true);
        let querySnapshot;
        try {
          querySnapshot = await getDocs(collection(db, 'book-shop'));
          if (querySnapshot.empty) {
            querySnapshot = await getDocs(collection(db, 'book_shop'));
          }
          if (querySnapshot.empty) {
            querySnapshot = await getDocs(collection(db, 'bookshops'));
          }
        } catch (e) {
          try {
            querySnapshot = await getDocs(collection(db, 'book_shop'));
          } catch (e2) {
            querySnapshot = await getDocs(collection(db, 'book-shop'));
          }
        }

        const fetched = [];
        if (querySnapshot && !querySnapshot.empty) {
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            const shopName = data.shop_name || data.name || data.title || "CSI Books Shop";
            const manager = data.manager || "Manager";
            const mobile = data.mobile || data.contact || data.phone || "";
            const address = data.address || "";
            const order = typeof data.order === 'number' ? data.order : 999;

            // Process facilities array or string
            let facilities = [];
            if (Array.isArray(data.facilities)) {
              facilities = data.facilities;
            } else if (typeof data.facilities === 'string' && data.facilities.trim()) {
              facilities = data.facilities.split(',').map(s => s.trim());
            }

            fetched.push({
              id: doc.id,
              shop_name: shopName,
              manager,
              mobile,
              address,
              facilities,
              order,
              raw: data
            });
          });

          fetched.sort((a, b) => a.order - b.order);
        }

        if (isMounted) {
          setShops(fetched);
        }
      } catch (err) {
        console.error("Error fetching book shops from Firebase:", err);
        if (isMounted) {
          setShops([]);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchBookShops();
    return () => { isMounted = false; };
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#5F5A58] font-sans flex flex-col pb-16">
      
      {/* Page Header */}
      <section className="relative bg-gradient-to-b from-[#FAF6F2] via-[#F5EFEE] to-[#E8DDD7] pt-28 sm:pt-32 pb-16 overflow-hidden border-b border-[#E8DDD7]">
        <div className="absolute right-[5%] top-1/2 -translate-y-1/2 pointer-events-none opacity-[0.03] select-none">
          <GiBookmarklet className="w-64 h-64 sm:w-96 sm:h-96 text-[#7B1E3A]" />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 sm:px-8 lg:px-12">
          <div className="w-16 h-1 bg-[#D4A64A] mb-6 rounded-full" />
          <h1 className="font-serif font-bold text-[#7B1E3A] text-[38px] sm:text-[50px] lg:text-[56px] uppercase tracking-[0.03em] leading-[1.05]">
            Diocesan Book Shop
          </h1>
          <p className="mt-4 max-w-xl text-[#7B6B66] text-[16px] leading-[1.6]">
            Official outlet for Christian literature, Bibles, hymnals, liturgical lectionaries, Sunday school curriculum, and altar supplies.
          </p>
        </div>
      </section>

      {/* Main Content Area */}
      <div className="max-w-[1440px] mx-auto px-6 sm:px-8 mt-12 w-full flex-grow space-y-8">

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[24px] border border-gray-100 shadow-sm">
            <LuLoader className="w-10 h-10 text-[#842343] animate-spin mb-3" />
            <p className="text-sm font-semibold text-[#5A4F4A]">Connecting to Firebase & loading book shop...</p>
          </div>
        ) : (
          shops.map((shop, sIdx) => {
            const cleanPhone = shop.mobile ? shop.mobile.replace(/\D/g, '') : '';

            return (
              <div
                key={shop.id || sIdx}
                className="bg-white rounded-[28px] border border-[#E8DDD7] shadow-[0_8px_30px_rgba(0,0,0,0.03)] hover:shadow-[0_16px_45px_rgba(123,30,58,0.08)] p-8 sm:p-10 relative overflow-hidden group transition-all"
              >
                {/* Decorative corner element */}
                <div className="absolute top-0 right-0 w-72 h-72 bg-gradient-to-br from-[#FAF6F2] via-[#F8EFEA] to-transparent rounded-bl-full opacity-60 pointer-events-none transition-transform duration-500 group-hover:scale-110" />

                <div className="relative z-10">
                  {/* Shop Header Bar */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5 mb-8 pb-6 border-b border-[#F0EBE6]">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-2xl bg-[#842343] text-white flex items-center justify-center shrink-0 shadow-md">
                        <LuShoppingBag className="w-8 h-8" />
                      </div>
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-[#842343] bg-[#842343]/10 px-2.5 py-0.5 rounded-full inline-block mb-1">
                          Official Diocesan Book Depot
                        </span>
                        <h2 className="font-serif font-bold text-[#842343] text-2xl sm:text-3xl leading-snug">
                          {shop.shop_name}
                        </h2>
                      </div>
                    </div>

                    {shop.mobile && (
                      <div className="flex items-center gap-2.5">
                        <a
                          href={`tel:${shop.mobile}`}
                          className="bg-[#842343] hover:bg-[#6c1b35] text-white text-xs font-bold py-2.5 px-4 rounded-xl shadow-xs transition-colors flex items-center gap-1.5"
                        >
                          <LuPhone className="w-3.5 h-3.5" />
                          <span>Call Shop</span>
                        </a>

                        <a
                          href={`https://wa.me/91${cleanPhone}`}
                          target="_blank"
                          rel="noreferrer"
                          className="bg-[#25D366] hover:bg-[#20bd5a] text-white text-xs font-bold py-2.5 px-4 rounded-xl shadow-xs transition-colors flex items-center gap-1.5"
                        >
                          <LuMessageCircle className="w-3.5 h-3.5" />
                          <span>WhatsApp</span>
                        </a>
                      </div>
                    )}
                  </div>

                  {/* 2-Column Grid: Left Contact & Info, Right Available Facilities */}
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                    
                    {/* Left: Personnel & Contact Info (Col 5) */}
                    <div className="lg:col-span-5 space-y-4">
                      {/* Manager */}
                      <div className="flex items-start gap-4 p-4 rounded-2xl bg-[#FAF7F2] border border-[#E8DDD7]">
                        <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-[#842343] shrink-0 border border-[#E8DDD7] shadow-2xs">
                          <LuUser className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-[#8A7E79]">Officer In Charge</p>
                          <p className="text-[#1a1715] font-serif font-bold text-base mt-0.5">{shop.manager}</p>
                        </div>
                      </div>

                      {/* Contact Number */}
                      {shop.mobile && (
                        <div className="flex items-start gap-4 p-4 rounded-2xl bg-[#FAF7F2] border border-[#E8DDD7]">
                          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-[#842343] shrink-0 border border-[#E8DDD7] shadow-2xs">
                            <LuPhone className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="text-[10px] font-bold uppercase tracking-wider text-[#8A7E79]">Contact Helpline</p>
                            <a href={`tel:${shop.mobile}`} className="text-[#842343] font-bold text-base mt-0.5 block hover:underline">
                              {shop.mobile}
                            </a>
                            <span className="text-[11px] text-[#8A7E79]">Available during working hours</span>
                          </div>
                        </div>
                      )}

                      {/* Location Address */}
                      <div className="flex items-start gap-4 p-4 rounded-2xl bg-[#FAF7F2] border border-[#E8DDD7]">
                        <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-[#842343] shrink-0 border border-[#E8DDD7] shadow-2xs">
                          <LuMapPin className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-[#8A7E79]">Store Location</p>
                          <p className="text-[#1a1715] font-semibold text-sm mt-0.5 leading-relaxed">{shop.address}</p>
                        </div>
                      </div>
                    </div>

                    {/* Right: Available Products & Facilities (Col 7) */}
                    <div className="lg:col-span-7 bg-[#FAF7F2] rounded-[24px] p-6 sm:p-7 border border-[#E8DDD7] shadow-2xs">
                      <div className="flex items-center justify-between gap-3 mb-5 pb-3 border-b border-[#E8DDD7]/70">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-[#842343] text-white flex items-center justify-center shadow-xs">
                            <GiOpenBook className="w-4 h-4" />
                          </div>
                          <h3 className="font-serif font-bold text-[#1a1715] text-lg">
                            Available Inventory &amp; Facilities
                          </h3>
                        </div>
                        <span className="text-xs text-[#8A7E79] font-semibold">
                          {shop.facilities.length} Categories
                        </span>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {shop.facilities.map((facility, fIdx) => (
                          <div
                            key={fIdx}
                            className="flex items-center gap-3 bg-white p-3.5 rounded-xl border border-[#E8DDD7]/80 shadow-2xs group/item hover:border-[#842343]/50 transition-colors"
                          >
                            <LuCircleCheck className="w-4 h-4 text-[#842343] shrink-0" />
                            <span className="text-xs sm:text-[13px] font-semibold text-[#3D332E] leading-snug">
                              {facility}
                            </span>
                          </div>
                        ))}
                      </div>

                      <div className="mt-6 pt-4 border-t border-[#E8DDD7]/70 flex items-center gap-2 text-xs text-[#8A7E79]">
                        <LuSparkles className="w-4 h-4 text-[#D4A64A] shrink-0" />
                        <span>Bulk orders for parishes, conventions, and Sunday schools can be placed directly via phone or WhatsApp.</span>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            );
          })
        )}

      </div>
    </div>
  );
}
